import { Cloud, HardDrive, Info, Layers, WifiOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Section, SectionHeading } from "@/components/ui/Section";
import { useLocale } from "@/i18n";
import { cn } from "@/lib/cn";

/**
 * The four concrete processing routes.
 *
 * Auto is described separately, below the grid, because it is not a route of
 * its own — it selects between these according to the user's policy.
 *
 * STATUS LABELS ARE A PRODUCT CLAIM. They must be re-verified before this site
 * is published. Cloud and Hybrid are marked "later" because ATHKYA's own
 * control plane does not exist yet; claiming otherwise would be false.
 * See docs/ATHKYA_REBRAND_AUDIT.md §8, Decision 1.
 */

type Status = "available" | "comingSoon";

export function ProcessingModes({ heading = true }: { heading?: boolean }) {
  const { t } = useLocale();

  const modes: Array<{
    icon: LucideIcon;
    name: string;
    body: string;
    requirement: string;
    status: Status;
    accent: "emerald" | "lavender";
  }> = [
    {
      icon: Cloud,
      ...t.modes.cloud,
      status: "comingSoon",
      accent: "lavender",
    },
    {
      icon: Layers,
      ...t.modes.hybrid,
      status: "comingSoon",
      accent: "lavender",
    },
    {
      icon: HardDrive,
      ...t.modes.local,
      status: "available",
      accent: "emerald",
    },
    {
      icon: WifiOff,
      ...t.modes.offline,
      status: "available",
      accent: "emerald",
    },
  ];

  return (
    <Section id="processing-modes">
      {heading ? (
        <SectionHeading title={t.modes.title} subtitle={t.modes.subtitle} />
      ) : null}

      <div className={cn("grid gap-5 md:grid-cols-2", heading && "mt-16")}>
        {modes.map(({ icon: Icon, name, body, requirement, status, accent }) => (
          <article key={name} className="card-surface flex flex-col p-7">
            <div className="flex items-start justify-between gap-4">
              <span
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-xl ring-1",
                  accent === "emerald"
                    ? "bg-emerald-500/10 ring-emerald-400/20"
                    : "bg-lavender-500/10 ring-lavender-400/20",
                )}
              >
                <Icon
                  size={19}
                  aria-hidden
                  className={
                    accent === "emerald" ? "text-emerald-300" : "text-lavender-300"
                  }
                />
              </span>

              <span
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs",
                  status === "available"
                    ? "border-emerald-400/25 bg-emerald-500/10 text-emerald-200"
                    : "border-white/12 bg-white/[0.03] text-cream-100/50",
                )}
              >
                {t.modes.status[status]}
              </span>
            </div>

            <h3 className="mt-5 text-lg font-semibold text-cream-50">{name}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-cream-100/58">
              {body}
            </p>

            <p className="mt-5 border-t border-white/8 pt-4 text-[0.8rem] text-cream-100/40">
              {requirement}
            </p>
          </article>
        ))}
      </div>

      <p className="mx-auto mt-8 flex max-w-2xl items-start gap-2.5 text-center text-sm text-cream-100/45">
        <Info size={15} className="mt-1 shrink-0 text-cream-100/35" aria-hidden />
        <span className="text-start">{t.modes.autoNote}</span>
      </p>
    </Section>
  );
}
